import { ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';
import { parallel as radashParallel } from 'radash';
import { filter, firstValueFrom, take } from 'rxjs';

import { FieldInterventionBloc } from '@connect-services/client/blocs/field-intervention.bloc';
import { FieldInterventionEntitiesListBloc } from '@connect-services/client/blocs/field-intervention-entities-list.bloc';
import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { AbstractLayerProjectComponent } from '@connect-services/client/components/abstract-layer-project/abstract-layer-project.component';
import { ModalType } from '@connect-services/client/components/abstract-modal/abstract-modal.component';
import { ConfirmationModal } from '@connect-services/client/components/confirmation-modal/confirmation-modal.component';
import { FieldInterventionInformationComponent } from '@connect-services/client/components/field-intervention-information/field-intervention-information.component';
import { FieldInterventionTitleComponent } from '@connect-services/client/components/field-intervention-title/field-intervention-title.component';
import { CreateEditFieldInterventionModalComponent } from '@connect-services/client/components/modals/create-edit-field-intervention-modal/create-edit-field-intervention-modal.component';
import {
    FieldInterventionEntityAttributions,
    FieldInterventionPlanningModalComponent,
    SelectState,
} from '@connect-services/client/components/modals/field-intervention-planning-modal/field-intervention-planning-modal.component';
import { NoDataToDisplayComponent } from '@connect-services/client/components/no-data-to-display/no-data-to-display.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import {
    FieldInterventionCreateEditModalData,
    FieldInterventionCreationEditionForm,
    FieldInterventionModalCloseModeEnum,
    FieldInterventionModalResult,
} from '@connect-services/client/models/create-edit-field-intervention-modal.model';
import { ActionsEnum, FieldInterventionEnum } from '@connect-services/client/models/panel.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { FieldInterventionEntitiesListComponent } from '@connect-services/client/pages/field-intervention/field-intervention-entities-list/field-intervention-entities-list.component';
import { FieldInterventionEntitiesListFooterComponent } from '@connect-services/client/pages/field-intervention/field-intervention-entities-list/field-intervention-entities-list-footer/field-intervention-entities-list-footer.component';
import { AssertService } from '@connect-services/client/services/assert.service';
import { ContentLayoutService, LAYOUT_KEY } from '@connect-services/client/services/content-layout.service';
import {
    EntitiesTableByLayerService,
    EntitiesTableTypeEnum,
} from '@connect-services/client/services/entities-table-by-layer.service';
import {
    FieldInterventionContextLayerOption,
    FieldInterventionService,
} from '@connect-services/client/services/field-intervention.service';
import {
    FieldInterventionAreaSelectionContextEnum,
    FieldInterventionAreaSelectionModeService,
} from '@connect-services/client/services/field-intervention-area-selection-mode.service';
import { FieldInterventionEntitiesFilterService } from '@connect-services/client/services/field-intervention-entities-filter.service';
import { FieldInterventionMapModeService } from '@connect-services/client/services/field-intervention-map-mode.service';
import { FieldInterventionMapRendererService } from '@connect-services/client/services/field-intervention-map-renderer.service';
import { FieldInterventionPermissionService } from '@connect-services/client/services/field-intervention-permission.service';
import { FilterResolverService } from '@connect-services/client/services/filter-resolver.service';
import { MapMaskService } from '@connect-services/client/services/map/mask/map-mask.service';
import { MapFieldInterventionService } from '@connect-services/client/services/map-field-intervention.service';
import { SelectZoneLayerService } from '@connect-services/client/services/select-zone-layer.service';
import { SourcesService } from '@connect-services/client/services/sources.service';
import { ZoneDelimiterService } from '@connect-services/client/services/zone-delimiter.service';
import { ButtonComponent, ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { CheckboxState } from '@connect-services/src/lib/components/checkbox/checkbox.component';
import { PaginatorComponent } from '@connect-services/src/lib/components/paginator/paginator.component';
import { TabComponent, TabWithState } from '@connect-services/src/lib/components/tab/tab.component';
import {
    FieldIntervention,
    FieldInterventionEntitiesService,
    FieldInterventionEntity,
    FieldInterventionsService,
    MultiPolygon,
    UpdateFieldInterventionDto,
} from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { TabContentDirective } from '@shared/client/directives/tab-content.directive';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ButtonComponent,
        FieldInterventionEntitiesListComponent,
        FieldInterventionEntitiesListFooterComponent,
        FieldInterventionInformationComponent,
        FieldInterventionTitleComponent,
        NoDataToDisplayComponent,
        RessourcesModule,
        TabComponent,
        TabContentDirective,
    ],
    selector: 'app-field-intervention-entity-planning',
    styleUrl: './field-intervention-entity-planning.component.scss',
    templateUrl: './field-intervention-entity-planning.component.html',
})
export class FieldInterventionEntityPlanningComponent extends AbstractLayerProjectComponent implements OnInit {
    public readonly contextLayersOptions = signal<FieldInterventionContextLayerOption[]>([]);
    public readonly contextLayersSelected = signal<FieldInterventionContextLayerOption[]>([]);
    public readonly description = signal<string>('');
    public readonly isContextLayersLoading = signal<boolean>(false);
    public readonly isContextLayersSaving = signal<boolean>(false);
    public readonly paginator = signal<PaginatorComponent | undefined>(undefined);

    protected readonly tabs = computed<TabWithState[]>(() =>
        this.entitiesTableByLayerService.layerNames().map((layerName: string) => ({ active: true, name: layerName })),
    );

    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected readonly ActionsEnum = ActionsEnum;
    protected readonly ButtonType = ButtonType;
    protected readonly entitiesTableByLayerService = inject(EntitiesTableByLayerService);
    protected readonly fieldInterventionBloc = inject(FieldInterventionBloc);
    protected readonly fieldInterventionEntitiesListBloc = inject(FieldInterventionEntitiesListBloc);
    protected readonly fieldInterventionPermissionService = inject(FieldInterventionPermissionService);
    protected readonly fieldInterventionService = inject(FieldInterventionService);
    protected readonly Object = Object;
    protected readonly RoutePathEnum = RoutePathEnum;

    private hasEditedFieldInterventionArea = false;

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private readonly contentLayoutService = inject(ContentLayoutService);
    private readonly fieldInterventionAreaSelectionModeService = inject(FieldInterventionAreaSelectionModeService);
    private readonly fieldInterventionEntitiesFilterService = inject(FieldInterventionEntitiesFilterService);
    private readonly fieldInterventionEntitiesService = inject(FieldInterventionEntitiesService);
    private readonly fieldInterventionMapModeService = inject(FieldInterventionMapModeService);
    private readonly fieldInterventionMapRendererService = inject(FieldInterventionMapRendererService);
    private readonly fieldInterventionsService = inject(FieldInterventionsService);
    private readonly filterResolverService = inject(FilterResolverService);
    private readonly mapFieldInterventionService = inject(MapFieldInterventionService);
    private readonly mapMaskService = inject(MapMaskService);
    private readonly matDialog = inject(MatDialog);
    private pendingFieldInterventionModalData: FieldInterventionCreateEditModalData | null = null;
    private readonly projectBloc = inject(ProjectBloc);
    private readonly sdkFieldInterventionService = inject(FieldInterventionsService);
    private readonly selectZoneLayerService = inject(SelectZoneLayerService);
    private readonly snackBarService = inject(SnackBarService);
    private readonly sourcesService = inject(SourcesService);
    private readonly translocoService = inject(TranslocoService);
    private readonly zoneDelimiterService = inject(ZoneDelimiterService);

    public constructor() {
        super();

        this.destroyRef.onDestroy(() => {
            this.destroyPlanningPage();
        });

        effect(() => {
            if (!this.fieldInterventionBloc.value() || !this.fieldInterventionEntitiesListBloc.value()?.items.length) {
                return;
            }

            this.fieldInterventionService.hasEntities.set(
                !!this.fieldInterventionEntitiesListBloc.value()!.items?.length,
            );
        });
    }

    public changeTab(layerNameIndex: number): void {
        const currentLayerName = this.entitiesTableByLayerService.layerNames()[layerNameIndex];

        this.entitiesTableByLayerService.resetPagination();
        this.entitiesTableByLayerService.setActiveLayerByName(currentLayerName);
    }

    public async goToEntitiesSelectionPage(): Promise<void> {
        if (!this.fieldInterventionPermissionService.canManageFieldIntervention()) {
            return;
        }

        const currentFieldInterventionEntityId = this.fieldInterventionService.getDetailPanelUuidFromUrl();
        if (currentFieldInterventionEntityId) {
            await this.fieldInterventionService.toggleFieldInterventionEntityDetailPanel(
                currentFieldInterventionEntityId,
            );
        }

        const path = [
            RoutePathEnum.PROJECTS,
            this.projectBloc.value()!.id,
            RoutePathEnum.FIELD_INTERVENTIONS,
            this.fieldInterventionBloc.value()!.id,
            RoutePathEnum.FIELD_INTERVENTION_CHANGE_SELECTION,
        ];

        await this.router.navigate(path);
        this.sourcesService.sources.set([]);
    }

    public launchFieldIntervention(): void {
        const dialogRef: MatDialogRef<ConfirmationModal> = this.matDialog.open(ConfirmationModal, {
            autoFocus: false,
            data: {
                cancelLabel: this.translocoService.translate('cancel'),
                confirmLabel: this.translocoService.translate('launch'),
                description: this.translocoService.translate('cannotEditOrAddEntities'),
                title: this.translocoService.translate('confirmLaunchIntervention'),
                type: ModalType.QUESTION,
            },
        });

        dialogRef
            .afterClosed()
            .pipe(filter((result: ModalResultEnum) => result === ModalResultEnum.CONFIRM))
            .subscribe(() => this.confirmFieldInterventionLaunch());
    }

    public async ngOnInit(): Promise<void> {
        this.fieldInterventionMapModeService.enterFieldInterventionEntitiesMode();
        this.entitiesTableByLayerService.isPlanningSelectionActive.set(true);

        this.entitiesTableByLayerService.isSourceDrawn.set(false);
        this.entitiesTableByLayerService.tableType.set(EntitiesTableTypeEnum.PLANNING);

        await this.entitiesTableByLayerService.initializeSelectOptions();

        await this.entitiesTableByLayerService.setLayers();
        this.entitiesTableByLayerService.initSelectionObjects(this.entitiesTableByLayerService.layers());

        await this.entitiesTableByLayerService.verifyEntitiesPlanned();

        await this.loadContextLayers();
    }

    public async onContextLayersSelectionChange(selectedOptionValues: string[]): Promise<void> {
        if (this.isContextLayersSaving()) {
            return;
        }

        const selectedOptions = this.contextLayersOptions().filter((option: FieldInterventionContextLayerOption) =>
            selectedOptionValues.includes(option.value),
        );

        const selectedLayerIds = this.fieldInterventionService.mapContextLayerOptionsToLayerIds(selectedOptions);

        this.isContextLayersSaving.set(true);

        try {
            await this.fieldInterventionService.updateFieldInterventionContextLayers(selectedLayerIds);
            this.contextLayersSelected.set(selectedOptions);
            this.snackBarService.openSuccessSnackBar('fieldInterventionEditionSuccess');
        } catch {
            this.snackBarService.openErrorSnackBar('errorPATCH');
            await this.loadContextLayers();
        } finally {
            this.isContextLayersSaving.set(false);
        }
    }

    public openFieldInterventionEditionModal(data?: FieldInterventionCreateEditModalData): void {
        if (!this.fieldInterventionPermissionService.canManageFieldIntervention()) {
            return;
        }

        const fieldIntervention: FieldIntervention | null = this.fieldInterventionBloc.value();
        if (!fieldIntervention) {
            return;
        }

        const modalData: FieldInterventionCreateEditModalData = data ?? {
            description: fieldIntervention.description ?? undefined,
            fieldInterventionArea: fieldIntervention.fieldInterventionArea,
            name: fieldIntervention.name,
            scheduledEndDate: fieldIntervention.scheduledEndDate
                ? new Date(fieldIntervention.scheduledEndDate)
                : undefined,
            title: 'editFieldIntervention',
        };

        const dialogRef: MatDialogRef<CreateEditFieldInterventionModalComponent, FieldInterventionModalResult | null> =
            this.matDialog.open<
                CreateEditFieldInterventionModalComponent,
                FieldInterventionCreateEditModalData,
                FieldInterventionModalResult | null
            >(CreateEditFieldInterventionModalComponent, {
                autoFocus: false,
                data: modalData,
                width: '40vw',
            });

        dialogRef.afterClosed().subscribe((result: FieldInterventionModalResult | null | undefined) => {
            void this.handleFieldInterventionEditionModalResult(result);
        });
    }

    public async openPlanningModal(): Promise<void> {
        if (!this.fieldInterventionPermissionService.canManageFieldIntervention()) {
            return;
        }

        const selectedEntities: FieldInterventionEntity[] = await this.getSelectedEntities();

        const preselectedPeopleIds = this.getUniqueAttributionIds(
            selectedEntities,
            (entity: FieldInterventionEntity) => entity.peopleIds ?? [],
        );

        const preselectedFormIds = this.getUniqueAttributionIds(
            selectedEntities,
            (entity: FieldInterventionEntity) => entity.formIds ?? [],
        );

        const preselectedPeopleCheckState: Record<string, SelectState> = this.buildPreselectedCheckState(
            selectedEntities,
            (entity: FieldInterventionEntity) => entity.peopleIds ?? [],
        );

        const preselectedFormCheckState: Record<string, SelectState> = this.buildPreselectedCheckState(
            selectedEntities,
            (entity: FieldInterventionEntity) => entity.formIds ?? [],
        );

        const dialogRef = this.matDialog.open(FieldInterventionPlanningModalComponent, {
            data: {
                hasLoader: true,
                isIntermediateStateEnabled: true,
                numberOfEntities: selectedEntities.length,
                preselectedFormCheckState,
                preselectedFormIds,
                preselectedPeopleCheckState,
                preselectedPeopleIds,
                startPlanning: (
                    attributions: FieldInterventionEntityAttributions,
                    report: (delta: number) => void,
                ): Promise<void> => this.planSelectedEntities(selectedEntities, attributions, report),
                updateMode: false,
            },
            width: '45rem',
        });

        dialogRef.afterClosed().subscribe(async (result?: FieldInterventionEntityAttributions): Promise<void> => {
            if (!result) {
                return;
            }

            await this.refreshPlanningAfterUpdate();
        });
    }

    protected onPaginatorChange(paginator: PaginatorComponent): void {
        this.entitiesTableByLayerService.paginator = paginator;
    }

    private buildPendingFieldInterventionModalData(
        form: FieldInterventionCreationEditionForm,
    ): FieldInterventionCreateEditModalData {
        return {
            description: form.controls.description.value ?? undefined,
            fieldInterventionArea: form.controls.fieldInterventionArea.value,
            name: form.controls.name.value,
            scheduledEndDate: form.controls.scheduledEndDate.value ?? undefined,
            title: 'editFieldIntervention',
        };
    }

    private buildPreselectedCheckState(
        selectedEntities: FieldInterventionEntity[],
        getIds: (entity: FieldInterventionEntity) => string[],
    ): Record<string, SelectState> {
        const ids = new Set(selectedEntities.flatMap((entity: FieldInterventionEntity) => getIds(entity)));

        return Object.fromEntries(
            [...ids].map((id: string) => {
                const assignedEntityCount: number = selectedEntities.filter((entity: FieldInterventionEntity) =>
                    getIds(entity).includes(id),
                ).length;

                return [
                    id,
                    {
                        canBeUnchecked: true,
                        checkState:
                            assignedEntityCount === selectedEntities.length
                                ? CheckboxState.CHECKED
                                : CheckboxState.INDETERMINATE,
                        isEnabled: true,
                    },
                ];
            }),
        );
    }

    private async confirmFieldInterventionLaunch(): Promise<void> {
        const fieldInterventionId: string | undefined = this.fieldInterventionBloc.value()?.id;

        AssertService.isString(fieldInterventionId);

        await firstValueFrom(
            this.sdkFieldInterventionService.fieldInterventionControllerPatch({
                id: fieldInterventionId,
                updateFieldInterventionDto: {
                    status: UpdateFieldInterventionDto.StatusEnum.ONGOING,
                },
            }),
        );

        const path = [
            RoutePathEnum.PROJECTS,
            this.projectBloc.value()?.id,
            RoutePathEnum.FIELD_INTERVENTIONS,
            fieldInterventionId,
            RoutePathEnum.FIELD_INTERVENTION_DASHBOARD,
        ];

        await this.router.navigate(path);
    }

    private destroyPlanningPage(): void {
        void this.selectZoneLayerService.removeLayers();

        this.fieldInterventionMapModeService.leaveFieldInterventionMapMode();
        this.mapMaskService.clearMasks();

        this.fieldInterventionService.resetHasEntities();

        this.entitiesTableByLayerService.resetData();
        this.resetFilterState();

        this.mapFieldInterventionService.defaultStyleFilterByLayer.clear();
    }

    private async finalizeFieldInterventionEdition(
        fieldInterventionId: string,
        fieldInterventionArea: MultiPolygon,
    ): Promise<void> {
        try {
            await this.fieldInterventionAreaSelectionModeService.refreshFieldInterventionArea(
                fieldInterventionId,
                fieldInterventionArea,
            );

            this.snackBarService.openSuccessSnackBar('fieldInterventionEditionSuccess');
        } catch {
            this.snackBarService.openErrorSnackBar('errorPATCH');
        }

        if (this.hasEditedFieldInterventionArea) {
            this.contentLayoutService.changeContentSize(
                FieldInterventionEnum.FIELD_INTERVENTION_ENTITY_PLANNING,
                LAYOUT_KEY.ONE_THIRD,
            );
        }

        this.hasEditedFieldInterventionArea = false;
        this.pendingFieldInterventionModalData = null;
    }

    private async getSelectedEntities(): Promise<FieldInterventionEntity[]> {
        const selectedEntities: FieldInterventionEntity[] = Object.values(
            this.entitiesTableByLayerService.selectedEntities(),
        ).flat();

        if (!this.entitiesTableByLayerService.fullSelectedLayers().length) {
            return selectedEntities;
        }

        const excludedIds = Object.values(this.entitiesTableByLayerService.unselectedEntities()).flatMap(
            (unselectedEntities: FieldInterventionEntity[]) =>
                unselectedEntities.map((unselectedEntity) => unselectedEntity.id),
        );

        const fullSelectedEntities: FieldInterventionEntity[] = await this.entitiesTableByLayerService.fetchAllEntities(
            excludedIds,
            this.entitiesTableByLayerService.fullSelectedLayers(),
        );

        return [...selectedEntities, ...fullSelectedEntities];
    }

    private getUniqueAttributionIds(
        selectedEntities: FieldInterventionEntity[],
        getIds: (entity: FieldInterventionEntity) => string[],
    ): string[] {
        return [...new Set(selectedEntities.flatMap((entity: FieldInterventionEntity) => getIds(entity)))];
    }

    private async handleFieldInterventionEditionModalResult(
        result: FieldInterventionModalResult | null | undefined,
    ): Promise<void> {
        if (!result) {
            this.pendingFieldInterventionModalData = null;
            this.hasEditedFieldInterventionArea = false;

            return;
        }

        if (!result.creationEditionForm) {
            return;
        }

        if (result.mode === FieldInterventionModalCloseModeEnum.FIELD_INTERVENTION_AREA_SELECTION) {
            this.startFieldInterventionAreaEdition(result.creationEditionForm);

            return;
        }

        await this.saveFieldInterventionEdition(result.creationEditionForm);
    }

    private async loadContextLayers(): Promise<void> {
        this.isContextLayersLoading.set(true);

        try {
            const { options, selectedOptions } = await this.fieldInterventionService.fetchContextLayerSelectionData();

            this.contextLayersOptions.set(options);
            this.contextLayersSelected.set(selectedOptions);
        } finally {
            this.isContextLayersLoading.set(false);
        }
    }

    private mergeAttributionIds<T extends { id: string }>(
        currentIds: string[],
        selectedAttributions: T[],
        removedAttributions: T[],
    ): string[] {
        const mergedIds = new Set(currentIds);

        for (const attribution of selectedAttributions) {
            mergedIds.add(attribution.id);
        }

        for (const attribution of removedAttributions) {
            mergedIds.delete(attribution.id);
        }

        return [...mergedIds];
    }

    private async patchFieldIntervention(
        fieldInterventionId: string,
        form: FieldInterventionCreationEditionForm,
    ): Promise<void> {
        const { description, fieldInterventionArea, name, scheduledEndDate } = form.controls;

        const updateFieldInterventionDto: UpdateFieldInterventionDto = {
            description: description.value,
            fieldInterventionArea: fieldInterventionArea.value ?? undefined,
            name: name.value,
            scheduledEndDate: scheduledEndDate.value ? scheduledEndDate.value.toISOString() : null,
        };

        await firstValueFrom(
            this.fieldInterventionsService.fieldInterventionControllerPatch({
                id: fieldInterventionId,
                updateFieldInterventionDto,
            }),
        );
    }

    private async planSelectedEntities(
        selectedEntities: FieldInterventionEntity[],
        attributions: FieldInterventionEntityAttributions,
        report: (delta: number) => void,
    ): Promise<void> {
        await radashParallel(5, selectedEntities, async (entity: FieldInterventionEntity) => {
            const forms = this.mergeAttributionIds(entity.formIds ?? [], attributions.forms, attributions.removedForms);

            const people = this.mergeAttributionIds(
                entity.peopleIds ?? [],
                attributions.people,
                attributions.removedPeople,
            );

            await firstValueFrom(
                this.fieldInterventionEntitiesService.fieldInterventionEntityControllerPatch({
                    id: entity.id,
                    updateFieldInterventionEntityDto: {
                        forms,
                        people,
                        status: FieldInterventionEntity.StatusEnum.PLANNED,
                    },
                }),
            );

            report(1);
        });
    }

    private async redrawFieldInterventionEntitySources(fitBounds = false): Promise<void> {
        const fieldInterventionSources = this.entitiesTableByLayerService.currentSources();

        await this.fieldInterventionMapRendererService.drawSourcesWithAreaMask(fieldInterventionSources, {
            fitBounds,
        });

        this.entitiesTableByLayerService.isSourceDrawn.set(true);
    }

    private async refreshPlanningAfterUpdate(): Promise<void> {
        this.entitiesTableByLayerService.fullSelectedLayers.set([]);
        this.entitiesTableByLayerService.resetSelectionObjects(this.entitiesTableByLayerService.layers());

        await this.entitiesTableByLayerService.resetTableData();
        await this.entitiesTableByLayerService.setCurrentSourcesAndEntityType();
        await this.entitiesTableByLayerService.verifyEntitiesPlanned();
        await this.redrawFieldInterventionEntitySources();

        this.entitiesTableByLayerService.manageHighlight();
    }

    private resetFilterState(): void {
        this.fieldInterventionEntitiesFilterService.resetData();
        this.filterResolverService.reset();
        this.filterResolverService.currentFilterType.set(null);
        this.zoneDelimiterService.resetZoneDelimiterData();
    }

    private async saveFieldInterventionEdition(form: FieldInterventionCreationEditionForm): Promise<void> {
        const fieldIntervention: FieldIntervention | null = this.fieldInterventionBloc.value();

        if (!fieldIntervention || !this.fieldInterventionPermissionService.canManageFieldIntervention()) {
            return;
        }

        const fieldInterventionArea: MultiPolygon | null = form.controls.fieldInterventionArea.value;

        if (!form.valid || !fieldInterventionArea) {
            return;
        }

        const pendingData: FieldInterventionCreateEditModalData = this.buildPendingFieldInterventionModalData(form);

        try {
            await this.patchFieldIntervention(fieldIntervention.id, form);
        } catch {
            this.pendingFieldInterventionModalData = pendingData;
            this.snackBarService.openErrorSnackBar('errorPATCH');
            this.openFieldInterventionEditionModal(pendingData);

            return;
        }

        await this.finalizeFieldInterventionEdition(fieldIntervention.id, fieldInterventionArea);
    }

    private startFieldInterventionAreaEdition(form: FieldInterventionCreationEditionForm): void {
        this.pendingFieldInterventionModalData = this.buildPendingFieldInterventionModalData(form);

        this.fieldInterventionAreaSelectionModeService.selectionConfirmed
            .pipe(take(1))
            .subscribe((fieldInterventionArea: MultiPolygon) => {
                if (!this.pendingFieldInterventionModalData) {
                    return;
                }

                this.pendingFieldInterventionModalData = {
                    ...this.pendingFieldInterventionModalData,
                    fieldInterventionArea,
                };

                this.hasEditedFieldInterventionArea = true;

                this.openFieldInterventionEditionModal(this.pendingFieldInterventionModalData);
            });

        this.fieldInterventionAreaSelectionModeService.open(FieldInterventionAreaSelectionContextEnum.EDIT_MODAL);
    }
}
